import { Observable } from "rxjs";
import { ActionColor, IFormAction } from "@src/app/shared/form/form.component";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { MyInjector } from "@src/app/app.module";
import { MAIL_STATUS, Person } from "@src/app/model/person";
import { MatDialog } from "@angular/material/dialog";
import { PersonService } from "@src/app/pages/person-page/person.service";
import { concatMap } from "rxjs/operators";
import { ConfirmDialogService } from "@src/app/shared/dialogs/confirmDialog";
import { Follower } from "@src/app/model/user";
import { ServiceRequest } from "@src/app/model/serviceRequest";
import { SendPersonMailComponent, SendPersonMailData } from "@src/app/shared/dialogs/send-person-mail/send-person-mail.component";

/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
export class SendEmailFormAction implements IFormAction {
    name = $localize`Send Email`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'send';
    approvalNeeded = false;
    disabled = false;
    approvalText = '';
    tipText: string;

    cds = MyInjector.instance.get(ConfirmDialogService);
    dialog = MyInjector.instance.get(MatDialog);
    personService = MyInjector.instance.get(PersonService);
    currentUser = MyInjector.instance.get(CurrentUserService).currentUser;

    private person: Person;
    private sr?: ServiceRequest;

    constructor(private f?: Follower, sr?: ServiceRequest) {
        if (f) {
            this.name = f.personFullName;
            this.tipText = $localize`Send email to ${f.personFullName}`;
            this.sr = sr;
        }
    }

    action(): Observable<ServiceRequest | Person> {
        if (this.f) {
            return this.personService.getOne<Person>(this.f.personId, null, this.f.personTeamId).pipe(
                concatMap(person => {
                    if (person) { this.person = person; }
                    return this.openSendMailDialog();
                })
            );
        }
        return this.openSendMailDialog();
    }

    setup(item: Person | ServiceRequest) {
        if ('lastName' in item) {
            this.person = item;
        } else {
            this.sr = item;
        }
        this.show = true;
        this.disabled = false;
        if (this.f && this.sr) {
            return; // tipText already set from the follower's name in the constructor
        }
        if (this.person?.email && Person.validEmail(this.person.email) && this.person.mailStatus === MAIL_STATUS.ok) {
            this.tipText = 'Send a mail to ' + this.person.fullName;
        } else {
            this.tipText = 'No valid email on file for ' + this.person?.fullName + ' — you can still attach a document';
        }
    }

    private openSendMailDialog(): Observable<ServiceRequest | Person> {
        const availableAttachments = (this.f && this.sr) ? this.sr.attachments : (this.person.attachments ?? []);
        return this.dialog.open(SendPersonMailComponent, {
            data: { person: this.person, sr: this.sr, availableAttachments } as SendPersonMailData,
        }).afterClosed();
    }
}
